import { SkyText } from "@skypointcloud/skypoint-ui";
import type { DataflowSummary } from "@shared/types/dataflows";
import { OVERVIEW_FAILING_SAMPLE_MAX, type OverviewConnections } from "@shared/types/overview";
import {
  connectionStatusOf,
  connectionStatusPresentation,
  type ConnectionStatus,
} from "@/components/connections/ConnectionStatus";
import { ROUTES, connectionPath } from "@/lib/routes";
import { ConnectionsIcon } from "@/lib/icons";
import { Fact, OverviewCard, OverviewLink, statusTone } from "./OverviewCard";

/**
 * Connections (contract §3, card 2): how many connections there are and how many read each of
 * the Connections list's status words, plus up to three failed connections by name, each a link
 * to its page.
 *
 * #1212: the counts are the Connections list's own (`connectionStatusOf`, one rule for the list,
 * the connection header and this card), so "Ready" here means what it means there: set up and
 * never imported. A connection whose last import succeeded is Active, never Ready. Until the list
 * has been read (or when it cannot be), the card shows only the server's counts that carry the
 * same meaning (Failed, Waiting, Running, Setup incomplete, Disabled), never a Ready it cannot
 * tell from Active.
 */

/** The order the words are shown in: what needs a person first, then the healthy and the idle. */
const SHOWN: ReadonlyArray<{ status: ConnectionStatus; always: boolean }> = [
  { status: "failed", always: true },
  { status: "waiting", always: false },
  { status: "running", always: true },
  { status: "active", always: true },
  { status: "ready", always: true },
  { status: "setup_incomplete", always: true },
  { status: "disabled", always: false },
];

export type ConnectionStatusCounts = Record<ConnectionStatus, number>;

/** Each status word's count over the list's rows, by the list's own rule. */
export function connectionStatusCounts(rows: readonly DataflowSummary[]): ConnectionStatusCounts {
  const counts: ConnectionStatusCounts = {
    setup_incomplete: 0,
    ready: 0,
    running: 0,
    waiting: 0,
    failed: 0,
    active: 0,
    disabled: 0,
  };
  for (const row of rows) counts[connectionStatusOf(row)] += 1;
  return counts;
}

/** The server's counts that mean what the list's words mean; Active and Ready are not among them. */
function serverCounts(connections: OverviewConnections): Partial<ConnectionStatusCounts> {
  return {
    failed: connections.failing,
    waiting: connections.waiting,
    running: connections.processing,
    setup_incomplete: connections.draft,
    disabled: connections.disabled,
  };
}

/**
 * Before the list is read: the connections that are neither failed, waiting, running, set up only
 * in part nor disabled, the server's nearest to the list's Active plus Ready, so the tone is not
 * green for an instance whose connections are all Setup incomplete or Running.
 */
function settledOf(connections: OverviewConnections): number {
  return Math.max(
    0,
    connections.total -
      connections.failing -
      connections.waiting -
      connections.processing -
      connections.draft -
      connections.disabled,
  );
}

interface FailedName {
  id: string;
  displayName: string;
}

/** When the connection's last import ended (or began, while it has no end), for "most recent". */
function lastImportAt(row: DataflowSummary): string {
  return row.lastRun?.finishedAt ?? row.lastRun?.startedAt ?? "";
}

/**
 * The failed connections the list shows, the most recent failure first (by its last import, not
 * by its last good refresh, so one that never succeeded is not ranked last), at most three.
 */
function failedNamesOf(rows: readonly DataflowSummary[], max: number): FailedName[] {
  return rows
    .filter((row) => connectionStatusOf(row) === "failed")
    .sort(
      (a, b) =>
        lastImportAt(b).localeCompare(lastImportAt(a)) ||
        a.displayName.localeCompare(b.displayName),
    )
    .slice(0, max)
    .map((row) => ({ id: row.id, displayName: row.displayName }));
}

export function ConnectionsCard({
  connections,
  list,
}: {
  connections: OverviewConnections;
  /** The Connections list's rows, once read. */
  list?: readonly DataflowSummary[];
}) {
  const counts: Partial<ConnectionStatusCounts> = list
    ? connectionStatusCounts(list)
    : serverCounts(connections);
  const total = list ? list.length : connections.total;
  const failed = counts.failed ?? 0;
  const names: FailedName[] = list
    ? failedNamesOf(list, OVERVIEW_FAILING_SAMPLE_MAX)
    : connections.failingSample.map((row) => ({ id: row.id, displayName: row.displayName }));

  return (
    <OverviewCard
      id="connections"
      title="Connections"
      icon={ConnectionsIcon}
      tone={statusTone({
        failed,
        warning: counts.waiting ?? 0,
        ok: list ? (counts.active ?? 0) + (counts.ready ?? 0) : settledOf(connections),
      })}
      testId="overview-connections"
      action={<OverviewLink to={ROUTES.connections}>Open Connections</OverviewLink>}
    >
      <dl className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-3 sm:grid-cols-3">
        <Fact term="Total" testId="overview-connections-total">
          {total.toLocaleString()}
        </Fact>
        {SHOWN.map(({ status, always }) => {
          const count = counts[status];
          if (count === undefined) return null;
          if (!always && count === 0) return null;
          return (
            <Fact
              key={status}
              term={connectionStatusPresentation(status).label}
              testId={`overview-connections-${status}`}
            >
              <span className={status === "failed" && count > 0 ? "text-destructive" : undefined}>
                {count.toLocaleString()}
              </span>
            </Fact>
          );
        })}
      </dl>
      {list ? null : (
        <SkyText size={200} color="muted" block testId="overview-connections-partial">
          Active and Ready show once the Connections list is read.
        </SkyText>
      )}

      {names.length > 0 ? (
        <div className="flex flex-col gap-1" data-testid="overview-connections-failing-sample">
          <SkyText size={200} color="muted" block>
            {failed > names.length ? `Failed, the ${names.length} most recent:` : "Failed:"}
          </SkyText>
          <ul className="flex flex-col gap-1">
            {names.map((row) => (
              <li key={row.id}>
                <OverviewLink
                  to={connectionPath(row.id)}
                  testId={`overview-failing-connection-${row.id}`}
                >
                  {row.displayName}
                </OverviewLink>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </OverviewCard>
  );
}
